import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { LocalNotifications } from '@capacitor/local-notifications'
import { PushNotifications } from '@capacitor/push-notifications'
import type { Router } from 'vue-router'
import { apiService } from '@/services/apiService'
import { parseInvitationPath } from '@/utils/invitationDeepLink'
import type { PortalNotification } from '@/services/notificationService'

export const CHANNEL_ID = 'mastermind_alerts'
const shownKeys = new Set<string>()
let channelReady = false
let routingBound = false

/**
 * google-services.json is now in android/app for project mastermind-coaching-6ffa4.
 * Keep this true only while that file is shipped with the Android build.
 */
const FIREBASE_PUSH_ENABLED = true

const hashId = (value: string) => {
  let hash = 0
  for (let i = 0; i < value.length; i += 1) {
    hash = ((hash << 5) - hash) + value.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash) % 2147483647 || 1
}

export const isNativeApp = () => Capacitor.isNativePlatform()

const webNotificationGranted = async (prompt = false) => {
  if (!('Notification' in window)) {
    return false
  }
  if (Notification.permission === 'granted') {
    return true
  }
  if (Notification.permission === 'denied' || !prompt) {
    return false
  }
  return (await Notification.requestPermission()) === 'granted'
}

const showWebNotification = async (title: string, body: string, prompt = false) => {
  if (!(await webNotificationGranted(prompt))) {
    return false
  }
  const notification = new Notification(title, {
    body,
    tag: 'mastermind-alert',
    requireInteraction: true,
    silent: false
  })
  window.setTimeout(() => notification.close(), 12000)
  return true
}

const ensureNativeChannel = async (prompt = false) => {
  try {
    const current = await LocalNotifications.checkPermissions()
    let display = current.display
    if (display !== 'granted' && prompt) {
      const permission = await LocalNotifications.requestPermissions()
      display = permission.display
    }
    if (display !== 'granted') {
      return false
    }

    if (!channelReady) {
      try {
        await LocalNotifications.createChannel({
          id: CHANNEL_ID,
          name: 'MasterMind alerts',
          description: 'Fee, attendance, holiday, and teacher updates',
          importance: 5,
          visibility: 1,
          vibration: true,
          lights: true
        })
      } catch {
        // Channel may already exist from a previous launch.
      }
      channelReady = true
    }

    return true
  } catch {
    return false
  }
}

export async function showNativeNotification(
  title: string,
  body: string,
  id = CHANNEL_ID,
  actionUrl = '',
  options?: { prompt?: boolean }
) {
  const prompt = options?.prompt === true
  if (!isNativeApp()) {
    return showWebNotification(title, body, prompt)
  }

  const allowed = await ensureNativeChannel(prompt)
  if (!allowed) {
    return false
  }

  try {
    await LocalNotifications.schedule({
      notifications: [{
        id: hashId(`${id}-${title}-${body}`),
        title,
        body,
        largeBody: body,
        channelId: CHANNEL_ID,
        smallIcon: 'ic_stat_notify',
        iconColor: '#07163A',
        extra: { actionUrl }
      }]
    })
    return true
  } catch {
    return false
  }
}

export async function sendTestNativeNotification() {
  const shown = await showNativeNotification(
    'MasterMind Coaching',
    'This is a phone notification. Fee, attendance, and holiday notices will appear here, outside the app.',
    `test-native-${Date.now()}`,
    '/',
    { prompt: true }
  )
  return {
    shown,
    message: shown
      ? 'A phone notification was sent. Check the status bar or lock screen — not only inside the app.'
      : 'Notification permission is off. Allow notifications for MasterMind in phone settings, then tap Test again.'
  }
}

export async function displayUnreadPortalNotifications(items: PortalNotification[]) {
  const unread = items.filter(item => !item.isRead && (item.priority === 'High' || item.type === 'FeeReminder' || item.type === 'TeacherFeedback' || item.type === 'Attendance' || item.type === 'InstituteNotice'))
  for (const item of unread) {
    if (shownKeys.has(item.id)) continue
    shownKeys.add(item.id)
    await showNativeNotification(item.title, item.message, item.id, item.actionUrl)
  }
}

export async function registerNativePush() {
  if (!isNativeApp()) {
    return
  }

  // Permission dialogs recreate MainActivity. Wait until login navigation has settled.
  await new Promise(resolve => window.setTimeout(resolve, 750))

  try {
    await ensureNativeChannel(false)
  } catch {
    // Local notifications are optional; login must still succeed.
  }

  if (!FIREBASE_PUSH_ENABLED || !Capacitor.isPluginAvailable?.('PushNotifications')) {
    return
  }

  try {
    const pushStatus = await PushNotifications.requestPermissions()
    if (pushStatus.receive !== 'granted') {
      return
    }
    await PushNotifications.register()
  } catch {
    // FCM needs google-services.json. Local notifications still display from the app.
  }
}

export function bindNativeNotificationRouting(router: Router) {
  if (!isNativeApp() || routingBound) {
    return
  }
  routingBound = true

  void App.addListener('appUrlOpen', ({ url }) => {
    const invitationPath = parseInvitationPath(url)
    if (invitationPath) {
      void router.push(invitationPath)
      return
    }
    try {
      const parsed = new URL(url.replace('mastermind://', 'https://app.local/'))
      const path = `${parsed.pathname}${parsed.search}`
      if (path && path !== '/') {
        void router.push(path)
      }
    } catch {
      // Ignore malformed deep links.
    }
  })

  void LocalNotifications.addListener('localNotificationActionPerformed', action => {
    const target = String(action.notification.extra?.actionUrl || '')
    if (target) {
      void router.push(target)
    }
  })

  if (!FIREBASE_PUSH_ENABLED || !Capacitor.isPluginAvailable?.('PushNotifications')) {
    return
  }

  try {
    void PushNotifications.addListener('registration', token => {
      void apiService.post('/notifications/device-token', {
        token: token.value,
        platform: 'android',
        deviceId: token.value.slice(0, 32)
      }).catch(() => undefined)
    })

    void PushNotifications.addListener('pushNotificationReceived', notification => {
      void showNativeNotification(
        notification.title || 'MasterMind Coaching',
        notification.body || '',
        notification.id || CHANNEL_ID,
        String(notification.data?.actionUrl || '')
      )
    })

    void PushNotifications.addListener('pushNotificationActionPerformed', action => {
      const target = String(action.notification.data?.actionUrl || '')
      if (target) {
        void router.push(target)
      }
    })
  } catch {
    // Push plugin is optional until Firebase is wired.
  }
}
